// components
import { StyledFlexMatchContainer } from 'components/flex-portal/FlexMatch/FlexMatch.styles';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { Icon } from '@sdb/planning-ui';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  AutomaticRosteringExplainPanel,
  AutomaticRosteringActions
} from './automatic-rostering';
import ChangesAfterPublicationPlaceholder from './components/ChangesAfterPublicationPlaceholder';
import NoAccessPage from './components/NoAccessPage';
import RosterEmployeesGrid from './components/RosterEmployeesGrid';
import RosterPlanToolbar from './components/RosterPlanToolbar';
import RosterOpenRequestsGrid from './components/RosterOpenRequestsGrid';

// constants
import { EMPTY_ROSTER_PLAN_FILTERS } from './RosterPlan.consts';

// Enums
import { UserPreferenceKeysEnums } from './RosterPlan.enums';

// hooks
import { useEffect, useRef, useState } from 'react';
import { useGetColumns } from './hooks/useGetColumns';
import useSyncHorizontalScroll from './hooks/useSyncHorizontalScroll';
import useRosterPlanQueryParams from './hooks/useRosterPlanQueryParams';
import useDatesToDisplay from './hooks/useDatesToDisplay';
import useGetOpenRequests from './hooks/useGetOpenRequests';
import useGetRosterPublishDate from './hooks/useGetRosterPublishDate';
import useGetEmployeesBasedOnCostCenters from './hooks/useGetEmployeesBasedOnCostCenters';
import useGetQualificationsByCostCenters from './hooks/useGetQualificationsByCostCenters';
import useFilteredRosterData from './hooks/useFilteredRosterData';
import useGroupedOpenRequests from './hooks/useGroupedOpenRequests';
import useRosterColumnGroupings from './hooks/useRosterColumnGroupings';
import useRosterPlanFilterOptions from './hooks/useRosterPlanFilterOptions';
import {
  useEmployeeNameFormat,
  useRosterPlanStore,
  useShowCommitmentCostCenter,
  useShowSortOrderColumn,
  useShowSourceCostCenter
} from './RosterPlan.stores';
import useRosterPlanPreferences from './hooks/useRosterPlanPreferences';
import useAutomaticRosteringToolbar from './hooks/useAutomaticRosteringToolbar';
import useRosterPlanMode from './hooks/useRosterPlanMode';
import useRosterPlanLiveUpdates from './hooks/useRosterPlanLiveUpdates';
import { useSelector } from 'hooks';
import { useForm } from 'react-hook-form';

// types
import { useGridApiRef } from '@mui/x-data-grid-pro';
import type {
  EmployeeNameFormatTypes,
  RosterPlanFilterSelection,
  RosterPlanFiltersForm
} from './RosterPlan.types';
import type { RootState } from 'redux/rootReducer';

// styles
import {
  StyledPanelsContainersBox,
  StyledResizeHandleBox,
  StyledResizeHandleDivider,
  StyledRosterPlanMainBox,
  StyledRosterPlanWorkspaceBox
} from './RosterPlan.styles';

// utils
import { getRosterPlanModeLayout } from './utils/getRosterPlanModeLayout';
import { getRosterRowClassName } from './utils/getRosterRowClassName';

const RosterPlan = () => {
  'use memo';

  const { hasReactRosterPlanViewAccess } = useSelector((state: RootState) => {
    return state.grants;
  });

  const resetRosterPlanState = useRosterPlanStore((state) => state.reset);
  const bottomPanelRef = useRef<ImperativePanelHandle>(null);
  const apiTop = useGridApiRef();
  const apiBottom = useGridApiRef();
  const [appliedFilters, setAppliedFilters] = useState<
    RosterPlanFilterSelection[]
  >([]);

  const period = useDateNavigationStore((state) => state.period);

  /**
   * The store is a module singleton, so it outlives the screen: without this, coming back from
   * another Dojo screen would render the grids for the plan unit the previous visit left behind
   * while the selector shows nothing.
   *
   * It has to clear on *unmount*, not on mount. `PlanUnitSelector` is uncontrolled here (no
   * `selectedValues` is passed) and restores its persisted cost centre selection itself, emitting
   * it from a child effect — which React flushes before this one. A reset on mount would therefore
   * erase that emission, and nothing replays it: the toolbar would show the remembered cost centre
   * next to two empty grids, the exact inverse of the intent. Clearing on the way out leaves every
   * mount starting from the defaults, with the selector's emission free to define the selection.
   *
   * `useRosterPlanPreferences` (#2481 item 4) owns the other half of the split: it applies the
   * stored preferences on mount via `applyStoredPreferences`, which touches the four mirrors only
   * and deliberately leaves the selections to this effect.
   */
  useEffect(() => {
    return () => {
      resetRosterPlanState();
    };
  }, [resetRosterPlanState]);

  const { showsRosterGrids } = getRosterPlanModeLayout(
    useRosterPlanMode().mode
  );

  const { params, options, startDate, endDate } = useRosterPlanQueryParams();

  // The grids' three queries wait while the grids are not shown, as the Dojo screen skipped its
  // roster reload in changes after publication: a month of a large tenant is not fetched for nothing.
  // The publish date stays on `options`; the info popover shows it in every mode.
  const gridQueryOptions = { enabled: options.enabled && showsRosterGrids };

  // Hooks run before the no-access return below, so the feature toggle is part of the guard: a
  // user without the React roster must not hold eventbus subscriptions for a screen they never see.
  useRosterPlanLiveUpdates({
    startDate,
    endDate,
    enabled: hasReactRosterPlanViewAccess && gridQueryOptions.enabled
  });

  const automaticRostering = useAutomaticRosteringToolbar({
    startDate,
    endDate
  });

  const { data: employees, isFetching: isFetchingEmployees } =
    useGetEmployeesBasedOnCostCenters(params, gridQueryOptions);

  const { data: rosterPublishDate } = useGetRosterPublishDate(params, options);

  const { data: openRequests, isFetching: isFetchingOpenRequests } =
    useGetOpenRequests(params, gridQueryOptions);

  const { employeesData, openRequestsData } = useFilteredRosterData({
    employees,
    openRequests,
    appliedFilters
  });

  const { data: qualificationsByCostCenters } =
    useGetQualificationsByCostCenters(params, gridQueryOptions);

  const filters = useRosterPlanFilterOptions({
    employees,
    openRequests,
    qualificationsByCostCenters
  });

  const filterMethods = useForm<RosterPlanFiltersForm>({
    defaultValues: EMPTY_ROSTER_PLAN_FILTERS
  });

  const {
    rosterPlanColumnsVisibility,
    dutiesFromOtherCostCenters,
    saveUserPreferences
  } = useRosterPlanPreferences();

  const showSortOrderColumn = useShowSortOrderColumn();
  const employeeNameFormat = useEmployeeNameFormat();
  const showSourceCostCenter = useShowSourceCostCenter();
  const showCommitmentCostCenter = useShowCommitmentCostCenter();

  const setEmployeeNameFormat = useRosterPlanStore(
    (state) => state.setEmployeeNameFormat
  );

  const groupOpenRequestsBasedOnFunction =
    useGroupedOpenRequests(openRequestsData);

  const datesToDisplay = useDatesToDisplay({ startDate, endDate });

  const columns = useGetColumns({
    datesToDisplay,
    period,
    isOpenRequests: false,
    qualificationsByCostCenters: qualificationsByCostCenters || [],
    showSourceCostCenter,
    showCommitmentCostCenter,
    costCenterFromOthersPrefValue: dutiesFromOtherCostCenters,
    rosterPlanColumnsVisibility,
    employeeNameFormat,
    showSortOrderColumn
  });

  const columnsOpenRequest = useGetColumns({
    datesToDisplay,
    period,
    isOpenRequests: true,
    qualificationsByCostCenters: qualificationsByCostCenters || [],
    rosterPlanColumnsVisibility,
    showSortOrderColumn
  });

  // Leaving changes after publication remounts both grids with new scroller nodes, which the day
  // count alone would not notice; `null` while they are away makes their return a change of key.
  useSyncHorizontalScroll(
    apiTop,
    apiBottom,
    showsRosterGrids ? datesToDisplay.length : null
  );

  const { employeeColumnGrouping, openRequestsColumnGrouping } =
    useRosterColumnGroupings(datesToDisplay);

  const handleCollapsiblePanel = () => {
    const panel = bottomPanelRef.current;
    if (!panel) return;
    panel.isCollapsed() ? panel.expand() : panel.collapse();
  };

  const handleSelectEmployeeNameFormat = (format: EmployeeNameFormatTypes) => {
    setEmployeeNameFormat(format);
    saveUserPreferences({
      key: UserPreferenceKeysEnums.RosterEmployeeNameDisplay,
      value: format
    });
  };

  if (!hasReactRosterPlanViewAccess) return <NoAccessPage />;

  return (
    <StyledFlexMatchContainer>
      {/* The panel is a sibling of the whole screen, toolbar included: opening it takes width away
          from the toolbar, which collapses its right-hand icons into the overflow menu rather than
          running them under the panel. */}
      <StyledRosterPlanWorkspaceBox>
        <StyledRosterPlanMainBox>
          <RosterPlanToolbar
            startDate={startDate}
            endDate={endDate}
            filters={filters}
            filterMethods={filterMethods}
            appliedFilters={appliedFilters}
            onApplyFilters={setAppliedFilters}
            onClearFilters={() => setAppliedFilters([])}
            rosterPublishDate={rosterPublishDate}
            beforeDatePicker={
              automaticRostering.actions && (
                <AutomaticRosteringActions {...automaticRostering.actions} />
              )
            }
          >
            {/* Top Roster Grid & Open Requests Grid */}
            <StyledPanelsContainersBox>
              {!showsRosterGrids && <ChangesAfterPublicationPlaceholder />}
              {showsRosterGrids && (
                <PanelGroup
                  direction="vertical"
                  style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
                >
                  <Panel
                    defaultSize={70}
                    minSize={15}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0
                    }}
                  >
                    <RosterEmployeesGrid
                      rows={employeesData ?? []}
                      columns={columns}
                      apiRef={apiTop}
                      isLoading={isFetchingEmployees}
                      getRowClassName={getRosterRowClassName}
                      columnGroupingModel={employeeColumnGrouping}
                      employeeNameFormat={employeeNameFormat}
                      onSelectEmployeeNameFormat={
                        handleSelectEmployeeNameFormat
                      }
                    />
                  </Panel>

                  <PanelResizeHandle onDoubleClick={handleCollapsiblePanel}>
                    <StyledResizeHandleBox>
                      <StyledResizeHandleDivider>
                        <Icon type="grip-dots-vertical" color="inherit" />
                      </StyledResizeHandleDivider>
                    </StyledResizeHandleBox>
                  </PanelResizeHandle>

                  <Panel
                    minSize={15}
                    collapsible
                    ref={bottomPanelRef}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0
                    }}
                  >
                    <RosterOpenRequestsGrid
                      rows={groupOpenRequestsBasedOnFunction}
                      columns={columnsOpenRequest}
                      apiRef={apiBottom}
                      isLoading={isFetchingOpenRequests}
                      getRowClassName={getRosterRowClassName}
                      columnGroupingModel={openRequestsColumnGrouping}
                    />
                  </Panel>
                </PanelGroup>
              )}
            </StyledPanelsContainersBox>
          </RosterPlanToolbar>
        </StyledRosterPlanMainBox>

        {automaticRostering.explainPanel && (
          <AutomaticRosteringExplainPanel
            key={automaticRostering.explainPanel.key}
            {...automaticRostering.explainPanel.props}
          />
        )}
      </StyledRosterPlanWorkspaceBox>
    </StyledFlexMatchContainer>
  );
};

export default RosterPlan;
